"use client";
import { Button, Input, Text } from "@softmax/design-system";
import { colors, spacing } from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useId, useRef } from "react";

export function MediaVideoInput({
  disabled,
  uploading,
  atMediaLimit,
  uploadingPoster,
  videoUrl,
  posterReady,
  videoError,
  onPosterFile,
  onVideoUrlChange,
  onAddVideo,
}: Readonly<{
  disabled: boolean;
  uploading: boolean;
  atMediaLimit: boolean;
  uploadingPoster: boolean;
  videoUrl: string;
  posterReady: boolean;
  videoError: string | null;
  onPosterFile(file: File): void;
  onVideoUrlChange(value: string): void;
  onAddVideo(): void;
}>) {
  const posterInput = useRef<HTMLInputElement>(null);
  const errorId = useId();
  return (
    <div {...stylex.props(styles.video)}>
      <Text size="label">Add video</Text>
      <div {...stylex.props(styles.videoInput)}>
        <Input
          disabled={disabled}
          type="url"
          value={videoUrl}
          aria-label="Video URL"
          aria-invalid={videoError !== null}
          aria-describedby={videoError ? errorId : undefined}
          placeholder="https://youtube.com/watch?v=…"
          onChange={(event) => onVideoUrlChange(event.target.value)}
        />
      </div>
      <Button
        size="sm"
        disabled={disabled || uploading || atMediaLimit}
        onClick={() => posterInput.current?.click()}
      >
        {uploadingPoster
          ? "Uploading poster…"
          : posterReady
            ? "Poster uploaded — choose a different one"
            : "Upload poster image (required)"}
      </Button>
      <input
        ref={posterInput}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        disabled={disabled || uploading || atMediaLimit}
        {...stylex.props(styles.hidden)}
        onChange={(event) => {
          const file = event.target.files?.[0];

          if (file) onPosterFile(file);

          event.target.value = "";
        }}
      />
      {videoError && (
        <div id={errorId} role="alert">
          <Text size="small" tone="danger">
            {videoError}
          </Text>
        </div>
      )}
      <Button
        size="sm"
        onClick={onAddVideo}
        disabled={
          disabled || uploading || atMediaLimit || !videoUrl || !posterReady
        }
      >
        Add video
      </Button>
    </div>
  );
}

const styles = stylex.create({
  video: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: spacing.space8,
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: colors.border,
    paddingTop: spacing.space10,
  },
  videoInput: { width: "100%" },
  hidden: { display: "none" },
});
